import { useState, useEffect } from "react";
import { BookOpen, Library, ChevronRight, ArrowLeft, BookMarked, Tag, Layers, List, Edit3 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/bookSelector.css";
import { API_BASE } from "../config";
import apiFetch from "../apiFetch";

export default function BookSelectorPage() {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const [catalogue, setCatalogue] = useState([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);
  const [genre, setGenre] = useState("");
  const [bookId, setBookId] = useState("");
  const [chapterId, setChapterId] = useState("");
  const [questionType, setQuestionType] = useState("mcq");

  useEffect(() => {
    apiFetch(`/api/books/catalogue`)
      .then((r) => r.json())
      .then((data) => {
        const books = data.books || [];
        setCatalogue(books);
        setLoadingCatalogue(false);

        // Pre-select book + chapter if arriving from the reading completion modal
        if (navState?.preBookId) {
          const match = books.find((b) => b.book_id === navState.preBookId);
          if (match) {
            setGenre(match.genre);
            setBookId(match.book_id);
          }
        }
        if (navState?.preChapterId) {
          setChapterId(navState.preChapterId);
        }
      })
      .catch(() => setLoadingCatalogue(false));
  }, []);

  const genres = [...new Set(catalogue.map((b) => b.genre))].sort();

  const booksForGenre = catalogue
    .filter((b) => b.genre === genre)
    .reduce((acc, b) => {
      if (!acc.find((x) => x.book_id === b.book_id)) {
        acc.push({ book_id: b.book_id, title: b.title });
      }
      return acc;
    }, []);

  const chaptersForBook = catalogue.filter((b) => b.book_id === bookId);

  const selectedEntry = catalogue.find(
    (b) => b.book_id === bookId && b.chapter_id === chapterId,
  );

  const handleGenreChange = (g) => {
    setGenre(g);
    setBookId("");
    setChapterId("");
  };

  const handleBookChange = (id) => {
    setBookId(id);
    setChapterId("");
  };

  const handleStart = () => {
    if (!bookId || !chapterId) return;
    navigate("/bookBasedComprehensionGame", {
      state: {
        fromGameSelect: true,
        bookId,
        chapterId,
        bookTitle: selectedEntry?.title || bookId,
        chapterDesc: selectedEntry?.chapter_desc || chapterId,
        questionType,
      },
    });
  };

  return (
    <div className="book-selector-page">
      <div className="book-selector-header">
        <button className="bsp-back-btn" onClick={() => navigate("/games/books")}>
          <ArrowLeft size={16} /> Back to Game Modes
        </button>
        <div className="bsp-title-row">
          <Library className="bsp-header-icon" size={32} />
          <div>
            <h1>Book Comprehension</h1>
            <p>Select a book and chapter to test your understanding</p>
          </div>
        </div>
      </div>

      {loadingCatalogue ? (
        <div className="bsp-loading">
          <BookOpen className="bsp-spin" size={32} />
          <p>Loading book catalogue...</p>
        </div>
      ) : (
        <div className="bsp-form-card">
          <div className="bsp-selector-group">
            <label className="bsp-label">
              <Tag size={14} /> Genre
            </label>
            <select
              className="bsp-select"
              value={genre}
              onChange={(e) => handleGenreChange(e.target.value)}
            >
              <option value="">— Select a genre —</option>
              {genres.map((g) => (
                <option key={g} value={g}>
                  {g.charAt(0).toUpperCase() + g.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className={`bsp-selector-group ${!genre ? "bsp-disabled" : ""}`}>
            <label className="bsp-label">
              <BookMarked size={14} /> Book
            </label>
            <select
              className="bsp-select"
              value={bookId}
              onChange={(e) => handleBookChange(e.target.value)}
              disabled={!genre}
            >
              <option value="">— Select a book —</option>
              {booksForGenre.map((b) => (
                <option key={b.book_id} value={b.book_id}>
                  {b.title}
                </option>
              ))}
            </select>
          </div>

          <div className={`bsp-selector-group ${!bookId ? "bsp-disabled" : ""}`}>
            <label className="bsp-label">
              <Layers size={14} /> Chapter
            </label>
            <select
              className="bsp-select"
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              disabled={!bookId}
            >
              <option value="">— Select a chapter —</option>
              {chaptersForBook.map((c) => {
                const label = `${c.chapter_id.replace("chapt-", "Chapter ")} — ${c.chapter_desc}`;
                return (
                  <option key={c.chapter_id} value={c.chapter_id}>
                    {label.length > 80 ? label.slice(0, 77) + "…" : label}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="bsp-selector-group">
            <label className="bsp-label">
              Question Type
            </label>
            <div className="bsp-radio-group">
              <label className={`bsp-radio-option ${questionType === "mcq" ? "bsp-radio-selected" : ""}`}>
                <input
                  type="radio"
                  name="questionType"
                  value="mcq"
                  checked={questionType === "mcq"}
                  onChange={() => setQuestionType("mcq")}
                />
                <List size={18} />
                <div className="bsp-radio-text">
                  <span>Multiple Choice</span>
                  <small>20 questions · 1 mark each · 20 min</small>
                </div>
              </label>
              <label className={`bsp-radio-option ${questionType === "written" ? "bsp-radio-selected" : ""}`}>
                <input
                  type="radio"
                  name="questionType"
                  value="written"
                  checked={questionType === "written"}
                  onChange={() => setQuestionType("written")}
                />
                <Edit3 size={18} />
                <div className="bsp-radio-text">
                  <span>Written Answers</span>
                  <small>20 questions · up to 70 marks · 45 min</small>
                </div>
              </label>
            </div>
          </div>

          {selectedEntry && (
            <div className="bsp-chapter-preview">
              <div className="bsp-preview-header">
                <BookOpen size={18} />
                <span className="bsp-preview-title">{selectedEntry.title}</span>
                <span className="bsp-preview-genre">{selectedEntry.genre}</span>
              </div>
              <p className="bsp-preview-desc">{selectedEntry.chapter_desc}</p>
              <div className="bsp-preview-levels">
                <span className="bsp-levels-label">Suitable levels:</span>
                {selectedEntry.suitable_levels.map((lvl) => (
                  <span key={lvl} className="bsp-level-badge">{lvl}</span>
                ))}
              </div>
            </div>
          )}

          <button
            className="bsp-start-btn"
            onClick={handleStart}
            disabled={!bookId || !chapterId}
          >
            <BookOpen size={18} />
            Start Reading Challenge
            <ChevronRight size={18} />
          </button>

          <p className="bsp-hint">
            {questionType === "mcq"
              ? "20 multiple choice questions · auto-marked · 20 minute timer"
              : "20 written answer questions · AI-marked · 45 minute timer"}
          </p>
        </div>
      )}
    </div>
  );
}
